import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { GitBranch, Plus, Play, Settings, Trash2 } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultStages = [
  { name: 'Build', type: 'build', status: 'pending' },
  { name: 'Test', type: 'test', status: 'pending' },
  { name: 'Security Scan', type: 'security', status: 'pending' },
  { name: 'Deploy', type: 'deploy', status: 'pending' },
  { name: 'Verify', type: 'verify', status: 'pending' }
];

export default function PipelinesPage() {
  const { getAuthHeaders } = useAuth();
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newPipeline, setNewPipeline] = useState({
    name: '',
    trigger: 'push',
    environment: 'staging',
    stages: defaultStages
  });

  const fetchPipelines = async () => {
    try {
      const response = await axios.get(`${API_URL}/pipelines`, {
        headers: getAuthHeaders()
      });
      setPipelines(response.data);
    } catch (error) {
      console.error('Failed to fetch pipelines:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPipelines();
  }, [getAuthHeaders]);

  const handleCreatePipeline = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/pipelines`, newPipeline, {
        headers: getAuthHeaders()
      });
      setPipelines([...pipelines, response.data]);
      setCreateDialogOpen(false);
      setNewPipeline({ name: '', trigger: 'push', environment: 'staging', stages: defaultStages });
      toast.success('Pipeline created successfully!');
    } catch (error) {
      toast.error('Failed to create pipeline');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div>
            <h1 className="font-heading text-3xl font-bold text-white" data-testid="pipelines-title">
              Pipelines
            </h1>
            <p className="text-foreground/60">Build and manage your deployment pipelines</p>
          </div>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-black font-bold hover:shadow-neon-cyan" data-testid="create-pipeline-btn">
                <Plus className="w-5 h-5 mr-2" />
                New Pipeline
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border" aria-describedby="create-pipeline-description">
              <DialogHeader>
                <DialogTitle className="text-white font-heading">Create Pipeline</DialogTitle>
                <p id="create-pipeline-description" className="sr-only">Form to create a new deployment pipeline.</p>
              </DialogHeader>
              <form onSubmit={handleCreatePipeline} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-foreground/80">Pipeline Name</Label>
                  <Input
                    value={newPipeline.name}
                    onChange={(e) => setNewPipeline({ ...newPipeline, name: e.target.value })}
                    placeholder="e.g., Production Deploy"
                    className="bg-background border-border"
                    required
                    data-testid="pipeline-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground/80">Trigger</Label>
                  <Select
                    value={newPipeline.trigger}
                    onValueChange={(value) => setNewPipeline({ ...newPipeline, trigger: value })}
                  >
                    <SelectTrigger className="bg-background border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="push">Git Push</SelectItem>
                      <SelectItem value="pull_request">Pull Request</SelectItem>
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="schedule">Schedule</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground/80">Target Environment</Label>
                  <Select
                    value={newPipeline.environment}
                    onValueChange={(value) => setNewPipeline({ ...newPipeline, environment: value })}
                  >
                    <SelectTrigger className="bg-background border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dev">Development</SelectItem>
                      <SelectItem value="staging">Staging</SelectItem>
                      <SelectItem value="production">Production</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground/80">Stages</Label>
                  <div className="p-4 bg-[#0A0E27] rounded-lg border border-border">
                    <div className="flex items-center gap-2 overflow-x-auto pb-2">
                      {newPipeline.stages.map((stage, i) => (
                        <div key={i} className="flex items-center">
                          <div className="px-3 py-1.5 bg-primary/10 border border-primary/30 rounded text-xs text-primary whitespace-nowrap">
                            {stage.name}
                          </div>
                          {i < newPipeline.stages.length - 1 && (
                            <div className="w-4 h-px bg-primary/50 mx-1"></div>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">Default pipeline with 5 stages</p>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-primary text-black font-bold" data-testid="create-pipeline-submit">
                  Create Pipeline
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </motion.div>

        {/* Pipelines Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : pipelines.length === 0 ? (
          <Card className="bg-card/50 border-border">
            <CardContent className="py-16 text-center">
              <GitBranch className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-heading font-semibold text-white mb-2">No Pipelines Yet</h3>
              <p className="text-muted-foreground mb-6">Create your first pipeline to automate deployments</p>
              <Button onClick={() => setCreateDialogOpen(true)} className="bg-primary text-black font-bold">
                <Plus className="w-5 h-5 mr-2" />
                Create Pipeline
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {pipelines.map((pipeline, i) => (
              <motion.div
                key={pipeline.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="bg-card/50 border-border hover:border-primary/30 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <GitBranch className="w-5 h-5 text-primary" />
                          <h3 className="font-heading font-semibold text-white text-lg">{pipeline.name}</h3>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                          <span className="px-2 py-0.5 bg-secondary/20 text-secondary rounded text-xs capitalize">
                            {pipeline.trigger}
                          </span>
                          <span>→</span>
                          <span className="capitalize">{pipeline.environment}</span>
                          <span>•</span>
                          <span>{pipeline.stages?.length || 0} stages</span>
                        </div>
                      </div>

                      {/* Pipeline visualization */}
                      <div className="flex-1 max-w-xl">
                        <div className="flex items-center gap-1 overflow-x-auto py-2">
                          {(pipeline.stages || defaultStages).map((stage, j) => (
                            <div key={j} className="flex items-center">
                              <div className={`
                                px-3 py-1.5 rounded text-xs whitespace-nowrap
                                ${stage.status === 'success' ? 'bg-accent/20 text-accent border border-accent/30' :
                                  stage.status === 'failed' ? 'bg-destructive/20 text-destructive border border-destructive/30' :
                                  stage.status === 'running' ? 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/30' :
                                  'bg-muted text-muted-foreground border border-border'}
                              `}>
                                {stage.name}
                              </div>
                              {j < (pipeline.stages || defaultStages).length - 1 && (
                                <div className="w-6 h-px bg-border mx-1"></div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm"
                          className="bg-accent text-black font-bold hover:shadow-neon-green"
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Run
                        </Button>
                        <Button size="sm" variant="outline" className="border-border text-foreground/60">
                          <Settings className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {pipeline.last_run && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <p className="text-xs text-muted-foreground">
                          Last run: {new Date(pipeline.last_run).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
